import React, { useEffect, useState } from 'react';
import {
  CtaButton,
  DatePickerInput,
  ModalContainer,
  SelectInput,
  TextInput,
  TogglerButtonInput,
} from '@tassomutuo-backoffice/ui';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import {
  DEAL_STAGE,
  DEAL_STAGE_LABEL_VALUE_OBJ,
  DealStage,
  Lead,
} from '@tassomutuo-backoffice/types';
import formatMoney, {
  formatNumberWithThousandsSeparator,
  removeThousandSeparator,
} from '../../../utils/formatMoney';
import { getMotivations } from '../../../utils/dropMotivation';
// import { da } from 'date-fns/locale';

interface IFormInputs {
  dealStage: DealStage;
  loanAmount: string;
  propertyValue: string;
  note: string;
  droppedMotivation: string;
  attoDate: Date | null;
  commission: string;
  appointmentDate: Date | null;
  appointmentTime: string | null;
  newAppointmentDate: Date | null;
  appointmentLocation: string;
  meeting_done: boolean;
  research: string;
  immobile_trovato: string;
  trattativa_voucher: string;
}

interface IProps {
  updating: DealStage;
  open: boolean;
  handleClose: () => void;
  lead?: Lead;
  updateLeadStatus: (data: IFormInputs) => Promise<void>;
}

const ModalLeadChangeStatusForm = ({
  updating,
  open,
  handleClose,
  lead,
  updateLeadStatus,
}: IProps) => {
  const [confirmForm, setConfirmForm] = useState(false);
  const [contactForm, setContactForm] = useState(false);
  const [showVoucherQuestion, setShowVoucherQuestion] = useState(false);
  const [isVoucherQuestionForInit, setIsVoucherQuestionForInit] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<IFormInputs | null>(null);
  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    reset,
    setValue,
    getValues,
  } = useForm<IFormInputs>({
    defaultValues: {
      dealStage: updating || lead?.properties.dealstage,
      loanAmount: lead?.properties.loan_amount
        ? formatNumberWithThousandsSeparator(
            formatMoney(+lead?.properties.loan_amount)
          )
        : '',
      propertyValue: lead?.properties.property_value
        ? formatNumberWithThousandsSeparator(
            formatMoney(+lead?.properties.property_value)
          )
        : '',
      immobile_trovato: lead?.properties?.immobile_trovato || 'false',
      research: lead?.properties?.research,
      trattativa_voucher: lead?.properties?.trattativa_voucher || '',
      note: '',
      droppedMotivation: '',
      attoDate: lead?.properties.data_atto_concluso
        ? new Date(lead?.properties.data_atto_concluso)
        : null,
      commission: lead?.properties.amount
        ? formatNumberWithThousandsSeparator(
            formatMoney(+lead?.properties.amount)
          )
        : '',
      appointmentDate: lead?.properties.appointment_date
        ? new Date(lead?.properties.appointment_date)
        : null,
      appointmentTime: lead?.properties.appointment_hour || '',
      appointmentLocation: lead?.properties.appointment_location || '',
      newAppointmentDate: null,
    },
  });

  useEffect(() => {
    if (!open) return;
    const stagesNeedingVoucherQuestion: DealStage[] = [
      DEAL_STAGE.INCARICO_O_VOUCHER_OTTENUTO,
      DEAL_STAGE.IN_ATTESA_DI_DELIBERA,
      DEAL_STAGE.DELIBERATA,
    ];
    const currentStage = lead?.properties.dealstage;
    const voucherFlag = lead?.properties.trattativa_voucher;
    if (
      currentStage &&
      stagesNeedingVoucherQuestion.includes(currentStage as DealStage) &&
      voucherFlag !== 'true' &&
      voucherFlag !== 'false'
    ) {
      setIsVoucherQuestionForInit(true);
      setShowVoucherQuestion(true);
    }
  }, [open]);

  useEffect(() => {
    if (lead) {
      console.log('lead?.properties.dealstage', lead?.properties.dealstage);
      reset({
        dealStage: updating || lead?.properties.dealstage,
        loanAmount: lead?.properties.loan_amount
          ? formatNumberWithThousandsSeparator(
              formatMoney(+lead?.properties.loan_amount)
            )
          : '',
        propertyValue: lead?.properties.property_value
          ? formatNumberWithThousandsSeparator(
              formatMoney(+lead?.properties.property_value)
            )
          : '',
        immobile_trovato: lead?.properties?.immobile_trovato || 'false',
        research: lead?.properties?.research,
        trattativa_voucher: lead?.properties?.trattativa_voucher || '',
        note: '',
        droppedMotivation: '',
        attoDate: lead?.properties.data_atto_concluso
          ? new Date(lead?.properties.data_atto_concluso)
          : null,
        commission: lead?.properties.amount
          ? formatNumberWithThousandsSeparator(
              formatMoney(+lead?.properties.amount)
            )
          : '',
        appointmentDate: lead?.properties.appointment_date
          ? new Date(lead?.properties.appointment_date)
          : null,
        appointmentTime: lead?.properties.appointment_hour || '',
        appointmentLocation: lead?.properties.appointment_location || '',
        newAppointmentDate: null,
      });
    }
  }, [lead]);

  const [isLoading, setIsLoading] = useState(false);

  const onCloseHandler = () => {
    handleClose();
    setConfirmForm(false);
    setShowVoucherQuestion(false);
    setIsVoucherQuestionForInit(false);
    setPendingSubmitData(null);
    reset({});
  };

  const VOUCHER_STAGE_MAP: Partial<Record<NonNullable<DealStage>, DealStage>> = {
    [DEAL_STAGE.INCARICO_O_VOUCHER_OTTENUTO]: DEAL_STAGE.INCARICO_VOUCHER_OTTENUTO,
    [DEAL_STAGE.IN_ATTESA_DI_DELIBERA]: DEAL_STAGE.IN_ATTESA_DELIBERA_VOUCHER,
    [DEAL_STAGE.DELIBERATA]: DEAL_STAGE.DELIBERATA_VOUCHER,
  };

  const doSubmit = async (data: IFormInputs) => {
    let dataToSend = { ...data };
    if (data.loanAmount) {
      dataToSend = { ...dataToSend, loanAmount: removeThousandSeparator(data.loanAmount) };
    }
    if (data.propertyValue) {
      dataToSend = { ...dataToSend, propertyValue: removeThousandSeparator(data.propertyValue) };
    }
    if (data.commission) {
      dataToSend = { ...dataToSend, commission: removeThousandSeparator(data.commission) };
    }
    try {
      setIsLoading(true);
      await updateLeadStatus(dataToSend);
      onCloseHandler();
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    console.log('data', data);
    if (
      lead?.properties.dealstage === DEAL_STAGE.IN_SVILUPPO &&
      data.dealStage !== DEAL_STAGE.IN_SVILUPPO &&
      !data.trattativa_voucher
    ) {
      setPendingSubmitData(data);
      setShowVoucherQuestion(true);
      return;
    }
    await doSubmit(data);
  };

  const currentDealStage = lead?.properties.dealstage;
  const isVoucher = watch('trattativa_voucher') === 'true';
  let statusOptionsAvailable: { label: string; value: string }[] = [];

  if (currentDealStage === DEAL_STAGE.APPUNTAMENTO_DA_RIFISSARE) {
    statusOptionsAvailable = [
      /*   DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_DA_CONFERMARE,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_CONFERMATO, */
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_DA_RIFISSARE,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_CONFERMATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.IN_SVILUPPO,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_ANNULLATO_STB_15GG,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.CONTATTARE_CLIENTE) {
    statusOptionsAvailable = [
      DEAL_STAGE_LABEL_VALUE_OBJ.CONTATTARE_CLIENTE,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_DA_CONFERMARE,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_CONFERMATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.IN_SVILUPPO,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_ANNULLATO_STB_15GG,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_DA_RIFISSARE,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.APPUNTAMENTO_DA_CONFERMARE) {
    statusOptionsAvailable = [
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_DA_CONFERMARE,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_CONFERMATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.IN_SVILUPPO,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_ANNULLATO_STB_15GG,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_DA_RIFISSARE,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.APPUNTAMENTO_CONFERMATO) {
    statusOptionsAvailable = [
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_CONFERMATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.IN_SVILUPPO,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_ANNULLATO_STB_15GG,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_DA_RIFISSARE,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.APPUNTAMENTO_ANNULLATO_STB_15GG) {
    statusOptionsAvailable = [
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_ANNULLATO_STB_15GG,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_DA_RIFISSARE,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_CONFERMATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.IN_SVILUPPO,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.IN_SVILUPPO) {
    statusOptionsAvailable = [
      DEAL_STAGE_LABEL_VALUE_OBJ.IN_SVILUPPO,
      isVoucher
        ? DEAL_STAGE_LABEL_VALUE_OBJ.INCARICO_VOUCHER_OTTENUTO
        : DEAL_STAGE_LABEL_VALUE_OBJ.INCARICO_O_VOUCHER_OTTENUTO,
      isVoucher
        ? DEAL_STAGE_LABEL_VALUE_OBJ.IN_ATTESA_DELIBERA_VOUCHER
        : DEAL_STAGE_LABEL_VALUE_OBJ.IN_ATTESA_DI_DELIBERA,
      isVoucher
        ? DEAL_STAGE_LABEL_VALUE_OBJ.DELIBERATA_VOUCHER
        : DEAL_STAGE_LABEL_VALUE_OBJ.DELIBERATA,
      DEAL_STAGE_LABEL_VALUE_OBJ.ATTO_FISSATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.INCARICO_O_VOUCHER_OTTENUTO) {
    statusOptionsAvailable = [
      isVoucher
        ? DEAL_STAGE_LABEL_VALUE_OBJ.INCARICO_VOUCHER_OTTENUTO
        : DEAL_STAGE_LABEL_VALUE_OBJ.INCARICO_O_VOUCHER_OTTENUTO,
      isVoucher
        ? DEAL_STAGE_LABEL_VALUE_OBJ.IN_ATTESA_DELIBERA_VOUCHER
        : DEAL_STAGE_LABEL_VALUE_OBJ.IN_ATTESA_DI_DELIBERA,
      isVoucher
        ? DEAL_STAGE_LABEL_VALUE_OBJ.DELIBERATA_VOUCHER
        : DEAL_STAGE_LABEL_VALUE_OBJ.DELIBERATA,
      DEAL_STAGE_LABEL_VALUE_OBJ.ATTO_FISSATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.INCARICO_VOUCHER_OTTENUTO) {
    statusOptionsAvailable = [
      DEAL_STAGE_LABEL_VALUE_OBJ.INCARICO_VOUCHER_OTTENUTO,
      DEAL_STAGE_LABEL_VALUE_OBJ.IN_ATTESA_DELIBERA_VOUCHER,
      DEAL_STAGE_LABEL_VALUE_OBJ.DELIBERATA_VOUCHER,
      DEAL_STAGE_LABEL_VALUE_OBJ.ATTO_FISSATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.IN_ATTESA_DI_DELIBERA) {
    statusOptionsAvailable = [
      isVoucher
        ? DEAL_STAGE_LABEL_VALUE_OBJ.IN_ATTESA_DELIBERA_VOUCHER
        : DEAL_STAGE_LABEL_VALUE_OBJ.IN_ATTESA_DI_DELIBERA,
      isVoucher
        ? DEAL_STAGE_LABEL_VALUE_OBJ.DELIBERATA_VOUCHER
        : DEAL_STAGE_LABEL_VALUE_OBJ.DELIBERATA,
      DEAL_STAGE_LABEL_VALUE_OBJ.ATTO_FISSATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.IN_ATTESA_DELIBERA_VOUCHER) {
    statusOptionsAvailable = [
      DEAL_STAGE_LABEL_VALUE_OBJ.IN_ATTESA_DELIBERA_VOUCHER,
      DEAL_STAGE_LABEL_VALUE_OBJ.DELIBERATA_VOUCHER,
      DEAL_STAGE_LABEL_VALUE_OBJ.ATTO_FISSATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.DELIBERATA) {
    statusOptionsAvailable = [
      isVoucher
        ? DEAL_STAGE_LABEL_VALUE_OBJ.DELIBERATA_VOUCHER
        : DEAL_STAGE_LABEL_VALUE_OBJ.DELIBERATA,
      DEAL_STAGE_LABEL_VALUE_OBJ.ATTO_FISSATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.DELIBERATA_VOUCHER) {
    statusOptionsAvailable = [
      DEAL_STAGE_LABEL_VALUE_OBJ.DELIBERATA_VOUCHER,
      DEAL_STAGE_LABEL_VALUE_OBJ.ATTO_FISSATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.ATTO_FISSATO) {
    statusOptionsAvailable = [
      DEAL_STAGE_LABEL_VALUE_OBJ.ATTO_FISSATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.ATTO_NOTARILE_CONCLUSO,
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
    ];
  }

  if (currentDealStage === DEAL_STAGE.ATTO_NOTARILE_CONCLUSO) {
    statusOptionsAvailable = [
      DEAL_STAGE_LABEL_VALUE_OBJ.ATTO_NOTARILE_CONCLUSO,
    ];
  }

  if (currentDealStage === DEAL_STAGE.DROPPED) {
    statusOptionsAvailable = [
      DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED,
      DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_CONFERMATO,
      DEAL_STAGE_LABEL_VALUE_OBJ.IN_SVILUPPO,
    ];
  }

  return (
    <ModalContainer
      open={open}
      handleClose={onCloseHandler}
      title={confirmForm || showVoucherQuestion ? '' : 'Fornisci aggiornamento'}
      stickyFooter
      containerSx={{
        maxWidth: '500px',
        maxHeight: {
          xs: 'calc(100dvh - 16px)',
          sm: 'calc(100dvh - 32px)',
        },
      }}
      mtHeader={0}
    >
      <Box
        width="100%"
        maxWidth="450px"
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        {confirmForm ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              {contactForm ? (
                <>
                  <h2 style={{ textAlign: 'center' }}>
                    Contatta il backoffice per ricevere supporto.
                  </h2>
                  <a
                    style={{
                      fontSize: '20px',
                      color: '#103c55',
                      fontWeight: 'bold',
                    }}
                    href="tel:+393921569517"
                  >
                    392 15 69 517
                  </a>
                  <a
                    style={{
                      fontSize: '20px',
                      color: '#103c55',
                      fontWeight: 'bold',
                    }}
                    href="mailto:michael.menchini@euroansa.it"
                  >
                    michael.menchini@euroansa.it
                  </a>
                  <Box display="flex" justifyContent="center" mt={4} gap={2}>
                    <CtaButton
                      variant="contained"
                      onClick={() => {
                        setContactForm(false);
                        setConfirmForm(false);
                        onCloseHandler();
                      }}
                    >
                      OK!
                    </CtaButton>
                  </Box>
                </>
              ) : (
                <>
                  <h2 style={{ textAlign: 'center' }}>
                    Hai incontrato la lead o ricevuto documenti dal cliente per
                    valutare la pratica?
                  </h2>
                  <Box display="flex" justifyContent="center" mt={4} gap={2}>
                    <CtaButton
                      variant="contained"
                      onClick={() => {
                        setContactForm(true);
                      }}
                    >
                      NO
                    </CtaButton>
                    <CtaButton
                      variant="contained"
                      // onSubmit={handleSubmit(onSubmit)}
                      onClick={() => {
                        setConfirmForm(false);
                      }}
                    >
                      SI
                    </CtaButton>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        ) : showVoucherQuestion ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <h2 style={{ textAlign: 'center' }}>È un incarico di Voucher/Predelibera?</h2>
            <Box display="flex" justifyContent="center" mt={4} gap={2}>
              <CtaButton
                variant="contained"
                onClick={async () => {
                  setShowVoucherQuestion(false);
                  if (isVoucherQuestionForInit) {
                    setIsVoucherQuestionForInit(false);
                    setValue('trattativa_voucher', 'false');
                  } else if (pendingSubmitData) {
                    const data = { ...pendingSubmitData, trattativa_voucher: 'false' };
                    setPendingSubmitData(null);
                    await doSubmit(data);
                  }
                }}
              >
                NO
              </CtaButton>
              <CtaButton
                variant="contained"
                onClick={async () => {
                  setShowVoucherQuestion(false);
                  if (isVoucherQuestionForInit) {
                    setIsVoucherQuestionForInit(false);
                    setValue('trattativa_voucher', 'true');
                    const currentStage = getValues('dealStage');
                    const voucherEquivalent = currentStage ? VOUCHER_STAGE_MAP[currentStage] : undefined;
                    if (voucherEquivalent) setValue('dealStage', voucherEquivalent);
                  } else if (pendingSubmitData) {
                    const voucherEquivalent = pendingSubmitData.dealStage ? VOUCHER_STAGE_MAP[pendingSubmitData.dealStage] : undefined;
                    const data = {
                      ...pendingSubmitData,
                      trattativa_voucher: 'true',
                      ...(voucherEquivalent && { dealStage: voucherEquivalent }),
                    };
                    setPendingSubmitData(null);
                    await doSubmit(data);
                  }
                }}
              >
                SI
              </CtaButton>
            </Box>
          </Box>
        ) : (
          <>
            <Controller
              control={control}
              name="dealStage"
              rules={{
                required: 'Seleziona',
              }}
              // shouldUnregister
              render={({ field }) => (
                <SelectInput
                  label="Stato pratica"
                  helperText={errors?.dealStage?.message}
                  error={!!errors?.dealStage}
                  selectOptions={statusOptionsAvailable}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    if (
                      e.target.value === DEAL_STAGE.IN_SVILUPPO &&
                      lead?.properties.dealstage !== DEAL_STAGE.IN_SVILUPPO
                    ) {
                      setConfirmForm(true);
                    }
                  }}
                />
              )}
            />
            {watch('dealStage') === DEAL_STAGE.DROPPED && (
              <>
                <Controller
                  control={control}
                  name="droppedMotivation"
                  rules={{
                    required: 'Seleziona una motivazione',
                  }}
                  shouldUnregister
                  render={({ field }) => (
                    <SelectInput
                      label="Motivazione"
                      helperText={errors?.droppedMotivation?.message}
                      error={!!errors?.droppedMotivation}
                      selectOptions={[
                        { label: 'Seleziona una motivazione', value: '' },
                        ...getMotivations(lead?.properties.dealstage),
                      ]}
                      {...field}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="meeting_done"
                  rules={{
                    validate: (value) =>
                      value === undefined || value === null
                        ? "Per favore seleziona un'opzione"
                        : true,
                  }}
                  shouldUnregister
                  render={({ field }) => (
                    <TogglerButtonInput
                      label="Appuntamento"
                      helperText={errors?.meeting_done?.message}
                      error={!!errors?.meeting_done}
                      selectOptions={[
                        { label: 'Svolto', value: 'true' },
                        { label: 'Non Svolto', value: 'false' },
                      ]}
                      {...field}
                    />
                  )}
                />
              </>
            )}

            {(watch('dealStage') === DEAL_STAGE.IN_SVILUPPO ||
              watch('dealStage') === DEAL_STAGE.INCARICO_O_VOUCHER_OTTENUTO ||
              watch('dealStage') === DEAL_STAGE.INCARICO_VOUCHER_OTTENUTO ||
              watch('dealStage') === DEAL_STAGE.IN_ATTESA_DI_DELIBERA ||
              watch('dealStage') === DEAL_STAGE.IN_ATTESA_DELIBERA_VOUCHER ||
              watch('dealStage') === DEAL_STAGE.ATTO_FISSATO) && (
              <>
                <Controller
                  control={control}
                  name="loanAmount"
                  rules={{
                    required: 'Campo obbligatorio',
                  }}
                  defaultValue={
                    lead?.properties.loan_amount
                      ? formatNumberWithThousandsSeparator(
                          formatMoney(+lead?.properties.loan_amount)
                        )
                      : ''
                  }
                  shouldUnregister
                  render={({ field }) => (
                    <TextInput
                      type="text"
                      label="Importo mutuo"
                      helperText={errors?.loanAmount?.message}
                      error={!!errors?.loanAmount}
                      showEuroIcon
                      {...field}
                      onChange={(e: { target: { value: string } }) =>
                        field.onChange(
                          formatNumberWithThousandsSeparator(e.target.value)
                        )
                      }
                      value={formatNumberWithThousandsSeparator(field.value)}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="propertyValue"
                  rules={{
                    required: 'Campo obbligatorio',
                  }}
                  defaultValue={
                    lead?.properties.property_value
                      ? formatNumberWithThousandsSeparator(
                          formatMoney(+lead?.properties.property_value)
                        )
                      : ''
                  }
                  shouldUnregister
                  render={({ field }) => (
                    <TextInput
                      type="text"
                      label="Valore immobile"
                      helperText={errors?.propertyValue?.message}
                      error={!!errors?.propertyValue}
                      showEuroIcon
                      {...field}
                      onChange={(e: { target: { value: string } }) =>
                        field.onChange(
                          formatNumberWithThousandsSeparator(e.target.value)
                        )
                      }
                      value={formatNumberWithThousandsSeparator(field.value)}
                    />
                  )}
                />
              </>
            )}

            {watch('dealStage') === DEAL_STAGE.APPUNTAMENTO_DA_RIFISSARE && (
              <Controller
                control={control}
                name="newAppointmentDate"
                rules={{
                  required: 'Campo obbligatorio',
                }}
                shouldUnregister
                render={({ field: { onChange, value } }) => (
                  <DatePickerInput
                    disabled={false}
                    label="Data nuovo ricontatto"
                    value={value}
                    onChangeValue={onChange}
                    helperText={errors?.newAppointmentDate?.message}
                    error={!!errors?.newAppointmentDate}
                  />
                )}
              />
            )}

            {watch('dealStage') === DEAL_STAGE.APPUNTAMENTO_CONFERMATO && (
              <>
                <Controller
                  control={control}
                  name="appointmentDate"
                  shouldUnregister
                  render={({ field: { onChange, value } }) => (
                    <DatePickerInput
                      disabled={false}
                      label="Data"
                      value={value}
                      onChangeValue={onChange}
                      helperText={errors?.appointmentDate?.message}
                      error={!!errors?.appointmentDate}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="appointmentTime"
                  rules={{
                    required: 'Campo obbligatorio',
                  }}
                  shouldUnregister
                  render={({ field }) => (
                    <TextInput
                      type="time"
                      label="Ora"
                      helperText={errors?.appointmentTime?.message}
                      error={!!errors?.appointmentTime}
                      {...field}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="appointmentLocation"
                  rules={{
                    required: 'Campo obbligatorio',
                  }}
                  defaultValue={lead?.properties.appointment_location}
                  shouldUnregister
                  render={({ field }) => (
                    <TextInput
                      type="text"
                      label="Luogo"
                      helperText={errors?.appointmentLocation?.message}
                      error={!!errors?.appointmentLocation}
                      {...field}
                    />
                  )}
                />
              </>
            )}

            {watch('dealStage') === DEAL_STAGE.ATTO_FISSATO && (
              <>
                <Controller
                  control={control}
                  name="attoDate"
                  rules={{
                    required: 'Campo obbligatorio',
                  }}
                  shouldUnregister
                  render={({ field: { onChange, value } }) => (
                    <DatePickerInput
                      disabled={false}
                      label="Data atto"
                      minDate={new Date(2011, 0, 1, 0, 0, 0, 0)} // CONTROLLO SULLA DATA PREGRESSA SE SERVE QUALCHE LIMITE
                      disablePast={false}
                      value={value}
                      onChangeValue={onChange}
                      helperText={errors?.attoDate?.message}
                      error={!!errors?.attoDate}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="commission"
                  rules={{
                    required: 'Campo obbligatorio',
                  }}
                  defaultValue={
                    lead?.properties.amount
                      ? formatNumberWithThousandsSeparator(
                          formatMoney(+lead?.properties.amount)
                        )
                      : ''
                  }
                  shouldUnregister
                  render={({ field }) => (
                    <TextInput
                      type="text"
                      label="Commissione inclusiva di assicurazione"
                      helperText={errors?.commission?.message}
                      error={!!errors?.commission}
                      showEuroIcon
                      {...field}
                      value={formatNumberWithThousandsSeparator(field.value)}
                    />
                  )}
                />
              </>
            )}

            {(
                <Controller
                  control={control}
                  name="immobile_trovato"
                  defaultValue="false"
                  rules={{
                    validate: (value) =>
                      value === undefined || value === null
                        ? "Per favore seleziona un'opzione"
                        : true,
                  }}
                  shouldUnregister
                  render={({ field }) => (
                    <TogglerButtonInput
                      label="Ha già individuato l'immobile da acquistare?"
                      helperText={errors?.immobile_trovato?.message}
                      error={!!errors?.immobile_trovato}
                      selectOptions={[
                        { label: 'Sì', value: 'true' },
                        { label: 'No', value: 'false' },
                      ]}
                      {...field}
                    />
                  )}
                />
              )}

            {watch('dealStage') !== DEAL_STAGE.ATTO_NOTARILE_CONCLUSO && (
              <Controller
                control={control}
                name="note"
                rules={{
                  required:
                    'Per favore inserisci le note. Per noi sono molto importanti per capire lo stato di avanzamento',
                }}
                shouldUnregister
                render={({ field }) => (
                  <TextInput
                    type="text"
                    label="Note"
                    helperText={errors?.note?.message}
                    error={!!errors?.note}
                    multiline
                    minRows={3}
                    {...field}
                  />
                )}
              />
            )}
            <Box
              data-modal-footer
              display="flex"
              justifyContent="center"
              mt={4}
            >
              <CtaButton variant="contained" type="submit" loading={isLoading}>
                SALVA
              </CtaButton>
            </Box>
          </>
        )}
      </Box>
    </ModalContainer>
  );
};

export default ModalLeadChangeStatusForm;
